import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import * as actions from '../../../../../store/index';
import { useDispatch, useSelector } from 'react-redux';
import ProjectionIcons from '../ProjectionIcons/ProjectionIcons';

const useStyles = makeStyles((theme) => ({
  ZoomView: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },

  topRow: {
    display: 'flex',
    alignSelf: 'end',
    margin: '0 auto min(0.6vh, 0.6vw) auto',
    position: 'relative',
    maxHeight: '50%',
    justifyContent: 'center',
  },

  bottomRow: {
    display: 'flex',
    alignSelf: 'start',
    margin: 'min(0.6vh, 0.6vw) auto 0 auto',
    position: 'relative',
    maxHeight: '50%',
    justifyContent: 'center',
  },

  topProjectionRow: {
    display: 'flex',
    alignSelf: 'end',
    margin: '0 auto min(0.3vh, 0.3vw) auto',
    position: 'relative',
    maxHeight: '50%',
    justifyContent: 'center',
    width: '100%',
  },

  bottomProjectionRow: {
    display: 'flex',
    alignSelf: 'start',
    margin: 'min(0.3vh, 0.3vw) auto 0 auto',
    position: 'relative',
    maxHeight: '50%',
    justifyContent: 'center',
    width: '100%',
  },

  projectionCell: {
    display: 'flex',
    transform: 'scale(1)',
    transition: '0.3s',
    width: '50%',
    maxWidth: '50%',
    height: '100%',
    borderRadius: '5px',
  },

  ZoomLeft: {
    zIndex: 50,
    opacity: 0,
    boxShadow: 'none',
    animation: '$appear 600ms forwards',
    background: theme.palette.accents.dark,
    height: '25px',
    width: '25px',
    minHeight: '25px',
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
      boxShadow: '0px 0px 5px -1px #FFFFF',
    },
  },

  ZoomRight: {
    zIndex: 50,
    opacity: 0,
    boxShadow: 'none',
    animation: '$appear 600ms forwards',
    background: theme.palette.accents.dark,
    height: '25px',
    width: '25px',
    minHeight: '25px',
    transform: 'scaleX(-1)',
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
      boxShadow: '0px 0px 5px -1px #FFFF',
    },
  },

  '@keyframes appear': {
    '70%': {
      opacity: 0,
    },
    '100%': {
      opacity: 0.8,
    },
  },

  zoomIcon: {
    color: 'white',
    fontSize: theme.fonts.responsiveHeader,
  },

  zoom: {
    transform: 'scale(2.02)',
    transition: '0.3s',
    zIndex: 80,
  },

  shrink: {
    transform: 'scale(0)',
    transition: '0.3s',
  },

  tooltipText: {
    letterSpacing: '0.1rem',
    fontSize: theme.fonts.responsiveMedium,
  },

  iconButtonContainer: {
    height: '100%',
    width: '10%',
    minWidth: 'min-content',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '2px 1px',
    backgroundColor: theme.palette.background.mainDark,
  },
}));

// Children from parent are always: rcc(0), lcc(1), rmlo(2), lmlo(3)
const childIndexByKey = { rcc: 0, lcc: 1, rmlo: 2, lmlo: 3 };

const projectionMeta = {
  rcc: { zoomBtnClass: 'ZoomLeft', btnOrder: 'start', propKey: 'projectionRcc' },
  lcc: { zoomBtnClass: 'ZoomRight', btnOrder: 'end', propKey: 'projectionLcc' },
  rmlo: { zoomBtnClass: 'ZoomLeft', btnOrder: 'start', propKey: 'projectionRmlo' },
  lmlo: { zoomBtnClass: 'ZoomRight', btnOrder: 'end', propKey: 'projectionLmlo' },
};

// transformOrigin is based on GRID POSITION, not projection name
const originByPosition = {
  'top-left': 'top left',
  'top-right': 'top right',
  'bottom-left': 'bottom left',
  'bottom-right': 'bottom right',
};

const layouts = {
  cc_top: {
    top: ['rcc', 'lcc'],
    bottom: ['rmlo', 'lmlo'],
  },
  mlo_top: {
    top: ['rmlo', 'lmlo'],
    bottom: ['rcc', 'lcc'],
  },
};

const ZoomView = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { zoomedProjection } = useSelector((state) => state.gui);
  const { inverted_view_position } = useSelector((state) => state.global_settings?.user_settings) || {};

  const layout = inverted_view_position ? layouts.mlo_top : layouts.cc_top;
  const childrenArray = React.Children.toArray(props.children);

  const getProjectionClasses = (key) => {
    const arr = [classes.projectionCell];
    if (zoomedProjection === key) arr.push(classes.zoom);
    else if (zoomedProjection) arr.push(classes.shrink);
    return arr.join(' ');
  };

  const setZoom = (proj) => {
    if (zoomedProjection === proj) dispatch(actions.setZoomedProjection(null));
    else dispatch(actions.setZoomedProjection(proj));
  };

  const renderProjectionCell = (key, position) => {
    const meta = projectionMeta[key];
    const hasProjection = props[meta.propKey];
    const child = childrenArray[childIndexByKey[key]];

    return (
      <div
        className={getProjectionClasses(key)}
        key={key}
        style={{
          justifyContent: zoomedProjection === key ? 'space-evenly' : 'center',
          transformOrigin: originByPosition[position],
        }}>
        {meta.btnOrder === 'start' && hasProjection && (
          <div className={classes.iconButtonContainer}>
            <ProjectionIcons
              projectionName={key}
              isLeft={key === 'lcc' || key === 'lmlo'}
              zoomBtnClass={meta.zoomBtnClass}
              isZoomed={zoomedProjection === key}
              onZoomClick={setZoom}
              invertedColor={props.invertedColor}
              setInvertedColor={props.setInvertedColor}
              setSecondCaptureOpen={props.setSecondCaptureOpen}
              secondCaptureOpen={props.secondCaptureOpen}
            />
          </div>
        )}
        {child}
        {meta.btnOrder === 'end' && hasProjection && (
          <div className={classes.iconButtonContainer}>
            <ProjectionIcons
              projectionName={key}
              isLeft={key === 'lcc' || key === 'lmlo'}
              zoomBtnClass={meta.zoomBtnClass}
              isZoomed={zoomedProjection === key}
              onZoomClick={setZoom}
              invertedColor={props.invertedColor}
              setInvertedColor={props.setInvertedColor}
              setSecondCaptureOpen={props.setSecondCaptureOpen}
              secondCaptureOpen={props.secondCaptureOpen}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={[classes.ZoomView, props.className].join(' ')}
      style={{ margin: zoomedProjection ? '8px' : '0px' }}>
      <div className={props.className ? classes.topProjectionRow : classes.topRow}>
        {layout.top.map((key, i) => renderProjectionCell(key, i === 0 ? 'top-left' : 'top-right'))}
      </div>
      <div className={props.className ? classes.bottomProjectionRow : classes.bottomRow}>
        {layout.bottom.map((key, i) => renderProjectionCell(key, i === 0 ? 'bottom-left' : 'bottom-right'))}
      </div>
    </div>
  );
};

export default ZoomView;
